import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InfraMap from '../components/InfraMap';
import { useTrips, useMovies, useJobs, jobIsActive } from '../data/firestore';
import {
  estimateCosts,
  fmtUSD,
  fmtBytes,
  jobActualCost,
  DEFAULT_ASSUMPTIONS,
  PRICES,
  INFRA_SPECS,
} from '../utils/cost';
import { updateTrip } from '../data/firestore';

const LS_KEY = 'cs-cost-assumptions';

function loadAssumptions() {
  try {
    return { ...DEFAULT_ASSUMPTIONS, ...JSON.parse(localStorage.getItem(LS_KEY) || '{}') };
  } catch {
    return { ...DEFAULT_ASSUMPTIONS };
  }
}

const BREAKDOWN_LABELS = {
  rawStorage: 'Raw footage storage (S3)',
  movieStorage: 'Published movie storage (S3)',
  deriveStorage: 'RAW develop / derived assets (S3)',
  streaming: 'Streaming egress (CloudFront)',
  requests: 'Requests (CloudFront/S3)',
  compute: 'Backend compute (Fargate, always-on)',
  loadBalancer: 'Load balancer (ALB)',
  other: 'Other (ECR, Secrets Manager)',
};

const BREAKDOWN_COLORS = {
  rawStorage: '#5b8def',
  movieStorage: '#14e0c8',
  deriveStorage: '#2bb6a3',
  streaming: '#e0a414',
  requests: '#f08a3c',
  compute: '#e0517a',
  loadBalancer: '#9b6cf2',
  other: '#3fbf6f',
};

function Stat({ label, value, sub }) {
  return (
    <div className="cs-cost-stat">
      <div className="cs-cost-stat-label">{label}</div>
      <div className="cs-cost-stat-value">{value}</div>
      {sub && <div className="cs-cost-stat-sub">{sub}</div>}
    </div>
  );
}

export default function Costs() {
  const { docs: trips } = useTrips();
  const { docs: movies } = useMovies();
  const jobs = useJobs(150);
  const jobStats = useMemo(() => {
    const list = jobs || [];
    const recent = list.slice(0, 25);
    const totalActual = list.reduce((s, j) => s + jobActualCost(j), 0);
    const active = list.filter(jobIsActive).length;
    return { recent, totalActual, active, count: list.length };
  }, [jobs]);
  const navigate = useNavigate();
  const [a, setA] = useState(loadAssumptions);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(a));
  }, [a]);

  const est = useMemo(
    () => estimateCosts(trips || [], movies || [], a),
    [trips, movies, a]
  );

  if (trips === null || movies === null) return <div className="cs-page">Loading…</div>;

  const setNum = (k) => (e) => setA((p) => ({ ...p, [k]: Number(e.target.value) }));

  return (
    <div className="cs-page">
      <div className="cs-page-head">
        <h1>Infrastructure cost</h1>
        <span className="cs-help">Estimated · us-east-1 on-demand pricing</span>
      </div>

      <div className="cs-cost-stats">
        <Stat label="Estimated monthly" value={fmtUSD(est.monthly)} sub={`${fmtUSD(est.monthly * 12)} / year`} />
        <Stat label="Estimated daily" value={fmtUSD(est.daily)} sub={`~${fmtUSD(est.monthly / 730)} / hour`} />
        <Stat
          label="Streaming / month"
          value={fmtUSD(est.breakdown.streaming)}
          sub={`${est.egressGb.toFixed(1)} GB egress · ${est.billableEgressGb.toFixed(1)} GB billable`}
        />
        <Stat
          label="Storage used"
          value={fmtBytes(est.rawBytes + est.movieBytes)}
          sub={`raw ${fmtBytes(est.rawBytes)} · movies ${fmtBytes(est.movieBytes)}`}
        />
      </div>

      {/* Services & infra map */}
      <div className="cs-section" style={{ marginTop: 8 }}>
        <div className="cs-section-head"><h2>Services &amp; infrastructure map</h2></div>
        <p className="cs-section-sub">
          Every service and how data flows between them — drag to explore, scroll to zoom.
        </p>
        <InfraMap />
      </div>

      {/* Infrastructure specs */}
      <div className="cs-section" style={{ marginTop: 8 }}>
        <div className="cs-section-head"><h2>Infrastructure specs</h2></div>
        <p className="cs-section-sub">
          The concrete AWS + Firebase resources behind these estimates — what's actually deployed and
          which cost line each one drives.
        </p>
        <table className="cs-table">
          <thead>
            <tr><th>Resource</th><th>Spec</th><th>Cost driver</th></tr>
          </thead>
          <tbody>
            {INFRA_SPECS.map((g) =>
              g.items.map((it, i) => (
                <tr key={`${g.group}-${it.name}`}>
                  <td>
                    {i === 0 && <span className="cs-spec-group">{g.group}</span>}
                    {it.name}
                  </td>
                  <td className="cs-spec-val">{it.spec}</td>
                  <td className="cs-help" style={{ padding: '8px 12px' }}>{it.driver}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Breakdown */}
      <div className="cs-section" style={{ marginTop: 8 }}>
        <div className="cs-section-head"><h2>Where the money goes</h2></div>
        <div className="cs-cost-bar">
          {Object.entries(est.breakdown).map(([k, v]) =>
            v > 0 ? (
              <div
                key={k}
                title={`${BREAKDOWN_LABELS[k]} — ${fmtUSD(v)}`}
                style={{ width: `${(v / est.monthly) * 100}%`, background: BREAKDOWN_COLORS[k] }}
              />
            ) : null
          )}
        </div>
        <table className="cs-table">
          <thead>
            <tr><th>Service</th><th className="num">Monthly</th><th className="num">Share</th></tr>
          </thead>
          <tbody>
            {Object.entries(est.breakdown)
              .sort((x, y) => y[1] - x[1])
              .map(([k, v]) => (
                <tr key={k}>
                  <td>
                    <span className="cs-dot" style={{ background: BREAKDOWN_COLORS[k] }} />
                    {BREAKDOWN_LABELS[k]}
                  </td>
                  <td className="num">{fmtUSD(v)}</td>
                  <td className="num">{est.monthly ? `${Math.round((v / est.monthly) * 100)}%` : '0%'}</td>
                </tr>
              ))}
            <tr className="cs-table-total">
              <td>Total</td>
              <td className="num">{fmtUSD(est.monthly)}</td>
              <td className="num">100%</td>
            </tr>
          </tbody>
        </table>
        {a.backendAlwaysOn && est.breakdown.compute + est.breakdown.loadBalancer > est.monthly * 0.5 && (
          <div className="cs-help" style={{ marginTop: 10 }}>
            💡 The always-on backend (Fargate + ALB) is{' '}
            {fmtUSD(est.breakdown.compute + est.breakdown.loadBalancer)}/mo and dominates the bill.
            Publishing, waveforms and thumbnails all run on this one task at no extra charge. Scaling
            the ECS service to 0 when you're not uploading/publishing would cut most of this.
          </div>
        )}
      </div>

      {/* Assumptions */}
      <div className="cs-section">
        <div className="cs-section-head"><h2>Assumptions</h2></div>
        <p className="cs-section-sub">
          Tune these to model your real usage — they only affect streaming/egress estimates and are
          saved in your browser.
        </p>
        <div className="cs-row-2">
          <div className="cs-field" style={{ margin: 0 }}>
            <label>Full movie views / month: {a.viewsPerMonth}</label>
            <input className="cs-slider" type="range" min="0" max="2000" step="10"
              value={a.viewsPerMonth} onChange={setNum('viewsPerMonth')} />
          </div>
          <div className="cs-field" style={{ margin: 0 }}>
            <label>Avg streaming bitrate: {a.avgViewMbps} Mbps</label>
            <input className="cs-slider" type="range" min="1" max="12" step="0.5"
              value={a.avgViewMbps} onChange={setNum('avgViewMbps')} />
          </div>
        </div>
        <label style={{ color: '#ccc', fontSize: 14, display: 'inline-block', marginTop: 12 }}>
          <input
            type="checkbox"
            checked={a.backendAlwaysOn}
            onChange={(e) => setA((p) => ({ ...p, backendAlwaysOn: e.target.checked }))}
          />{' '}
          Backend (Fargate) running 24/7
        </label>
        <div className="cs-help" style={{ marginTop: 8 }}>
          Storage {fmtUSD(PRICES.s3StandardGbMonth)}/GB-mo (S3 Standard) · egress{' '}
          {fmtUSD(PRICES.cloudfrontEgressGb)}/GB after {PRICES.cloudfrontFreeGb} GB/mo free ·{' '}
          first 10M CloudFront requests/mo free · Firebase (Hosting/Auth/Firestore) assumed on the
          free Spark tier. Surf-conditions data (Open-Meteo, free; Stormglass tides, free tier ~10
          req/day) is ~2 cached calls per trip — effectively $0.
        </div>
        <div className="cs-help" style={{ marginTop: 6 }}>
          At {a.viewsPerMonth} views/mo you'd serve ~{est.egressGb.toFixed(1)} GB egress — gross{' '}
          {fmtUSD(est.grossStreaming)}/mo, but the 1 TB free tier covers{' '}
          {est.billableEgressGb <= 0 ? 'all of it (so $0 today)' : `most of it (${fmtUSD(est.breakdown.streaming)} billable)`}.
        </div>
      </div>

      {/* Lifetime storage projection */}
      <div className="cs-section">
        <div className="cs-section-head"><h2>Lifetime storage — keeping the raw archive</h2></div>
        <p className="cs-section-sub">
          What it costs to preserve your current {fmtBytes(est.rawBytes)} of raw footage long-term,
          by S3 storage class. Cheaper classes trade instant access and add per-GB retrieval fees +
          minimum-storage durations — but for a lifetime archive that's rarely re-downloaded, the
          savings compound.
        </p>
        <table className="cs-table">
          <thead>
            <tr>
              <th>Storage class</th>
              <th className="num">$/GB-mo</th>
              <th className="num">Monthly</th>
              <th className="num">Yearly</th>
              <th className="num">10-year</th>
            </tr>
          </thead>
          <tbody>
            {est.storageProjection.map((c) => (
              <tr key={c.id}>
                <td title={c.note}>
                  {c.label}
                  {(trips || []).every((t) => (t.storageClass || 'STANDARD') === c.id) && est.rawBytes > 0 && (
                    <span className="cs-badge" style={{ marginLeft: 6 }}>current</span>
                  )}
                </td>
                <td className="num">{fmtUSD(c.price)}</td>
                <td className="num">{fmtUSD(c.perMonth)}</td>
                <td className="num">{fmtUSD(c.perYear)}</td>
                <td className="num">{fmtUSD(c.tenYear)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="cs-help" style={{ marginTop: 8 }}>
          Tip: <strong>Glacier Instant Retrieval</strong> keeps millisecond access at ~
          {Math.round((1 - PRICES.s3GlacierIrGbMonth / PRICES.s3StandardGbMonth) * 100)}% off Standard —
          ideal for irreplaceable originals you rarely open but want instantly when you do. Edited
          movies you stream stay on Standard.
        </div>
      </div>

      {/* Per-trip */}
      <div className="cs-section">
        <div className="cs-section-head"><h2>Cost per trip (storage)</h2></div>
        <p className="cs-section-sub">
          Ongoing cost to keep each trip's raw media at full resolution. Change a trip's storage
          class to model archive savings (estimate only — physically moving objects to Glacier would
          need an S3 lifecycle rule).
        </p>
        {est.perTrip.length === 0 ? (
          <div className="cs-empty">No trips yet.</div>
        ) : (
          <table className="cs-table">
            <thead>
              <tr><th>Trip</th><th className="num">Files</th><th className="num">Stored</th><th>Class</th><th className="num">Monthly</th><th className="num">Yearly</th></tr>
            </thead>
            <tbody>
              {est.perTrip.map((t) => (
                <tr key={t.id}>
                  <td className="cs-table-click" onClick={() => navigate(`/trips/${t.id}`)}>{t.name}</td>
                  <td className="num">{t.assetCount}</td>
                  <td className="num">{fmtBytes(t.bytes)}</td>
                  <td>
                    <select
                      className="cs-input cs-input-sm"
                      value={t.storageClass}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => updateTrip(t.id, { storageClass: e.target.value })}
                      title="Storage class used for cost estimates (modeling only — doesn't move S3 objects yet)"
                    >
                      {est.storageProjection.map((c) => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="num">{fmtUSD(t.monthly)}</td>
                  <td className="num">{fmtUSD(t.yearly)}</td>
                </tr>
              ))}
              <tr className="cs-table-total">
                <td>All trips</td>
                <td className="num">{est.perTrip.reduce((s, t) => s + t.assetCount, 0)}</td>
                <td className="num">{fmtBytes(est.rawBytes)}</td>
                <td />
                <td className="num">{fmtUSD(est.breakdown.rawStorage)}</td>
                <td className="num">{fmtUSD(est.breakdown.rawStorage * 12)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Per-movie */}
      <div className="cs-section">
        <div className="cs-section-head"><h2>Cost per movie (storage + streaming)</h2></div>
        <p className="cs-section-sub">
          Published movies store an HLS ladder and incur egress when watched (at {a.viewsPerMonth}{' '}
          views/mo). Analyzing a movie for social reframing also keeps a 1080p master MP4 (folded
          into Storage/mo). "Publish" and "Reframe" are one-time compute that runs on the always-on
          backend, so marginal cash cost is ~$0 (shown for reference). Higher Output Quality (4K /
          “Max” RAW re-decode, 60fps, and the downloadable master) takes much longer to render and
          stores a larger master file, but the compute still runs on the same always-on task.
          When ephemeral fan-out is enabled, a heavy RED decode is split across up to ~32 short-lived
          Fargate tasks (8 vCPU each) so it finishes in minutes instead of hours — those tasks bill
          per-second only while running (a few cents of vCPU·hours per clip), and the always-on box
          can then be sized down since the heavy work no longer lives on it.
        </p>
        {est.perMovie.length === 0 ? (
          <div className="cs-empty">No movies yet.</div>
        ) : (
          <table className="cs-table">
            <thead>
              <tr><th>Movie</th><th className="num">Length</th><th className="num">HLS size</th><th className="num">Storage/mo</th><th className="num">Streaming/mo</th><th className="num">Publish (1×)</th><th className="num">Reframe (1×)</th><th className="num">Monthly</th></tr>
            </thead>
            <tbody>
              {est.perMovie.map((m) => (
                <tr key={m.id} className="cs-table-click" onClick={() => navigate(`/watch/${m.id}`)}>
                  <td>
                    {m.title}
                    {!m.published && <span className="cs-badge" style={{ marginLeft: 6 }}>unpublished</span>}
                    {m.analyzed && <span className="cs-badge" style={{ marginLeft: 6 }}>reframed</span>}
                  </td>
                  <td className="num">{Math.round(m.durationSec)}s</td>
                  <td className="num">{m.storedBytes ? fmtBytes(m.storedBytes) : '—'}</td>
                  <td className="num">{fmtUSD(m.storage)}</td>
                  <td className="num">{fmtUSD(m.stream)}</td>
                  <td className="num">{m.published ? fmtUSD(m.publishOnce) : '—'}</td>
                  <td className="num">{m.analyzed ? fmtUSD(m.analyzeOnce) : '—'}</td>
                  <td className="num">{fmtUSD(m.monthly)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="cs-section">
        <div className="cs-section-head">
          <h2>Processing jobs (metered)</h2>
          {jobStats.active > 0 && <span className="cs-badge">{jobStats.active} live</span>}
        </div>
        <p className="cs-section-sub">
          Per-job cost of the heavy media work — RED decode and RAW re-decode (short-lived Fargate
          tasks) plus movie publishing (always-on task, metered by wallclock) — from actual runtime ×
          vCPU/GB-seconds. Estimated cost is shown at submission; actual is what really billed. Total
          metered so far:{' '}
          <strong>{fmtUSD(jobStats.totalActual)}</strong> across {jobStats.count} job(s). Manage live
          jobs on the <a href="/jobs">Processes</a> page.
        </p>
        {(!jobs || jobs.length === 0) ? (
          <div className="cs-empty">No processing jobs yet.</div>
        ) : (
          <table className="cs-table">
            <thead>
              <tr><th>Job</th><th>Type</th><th>Status</th><th className="num">Chunks</th><th className="num">Task-sec</th><th className="num">Est.</th><th className="num">Actual</th></tr>
            </thead>
            <tbody>
              {jobStats.recent.map((j) => (
                <tr key={j.id} className="cs-table-click" onClick={() => navigate('/jobs')}>
                  <td>{j.title || j.id}</td>
                  <td>{j.type === 'publish' ? 'Publish encode' : j.type === 'r3d-master' ? 'RAW re-decode' : 'RED decode'}</td>
                  <td>{j.paused ? 'paused' : j.status}</td>
                  <td className="num">{j.chunks || 0}</td>
                  <td className="num">{j.cost?.taskSeconds || 0}</td>
                  <td className="num">{j.cost?.estimatedUsd != null ? fmtUSD(j.cost.estimatedUsd) : '—'}</td>
                  <td className="num">{fmtUSD(jobActualCost(j))}{jobIsActive(j) ? '…' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="cs-help" style={{ marginTop: 8 }}>
        Estimates only. Actual AWS charges depend on real egress, request counts, region and current
        list prices. They exclude one-off items like the GoDaddy domain and any taxes.
      </div>
    </div>
  );
}
